import { FC, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { TopBar } from "components/navigation/TopBar"
import { TournamentAnalyticsCards } from "components/tournaments/monitor/TournamentAnalyticsCards"
import { TournamentHeader } from "components/tournaments/monitor/TournamentHeader"
import { TournamentLeaderboard } from "components/tournaments/monitor/TournamentLeaderboard"
import { TournamentReferralActivity } from "components/tournaments/monitor/TournamentReferralActivity"
import { TournamentChatMonitor } from "components/tournaments/TournamentChatMonitor"

import { TournamentService } from "services/tournaments/tournament.service"

import { Tournament, TournamentAnalytics, TournamentParticipant, TournamentReferralData } from "types/tournament.types"

export const TournamentMonitor: FC = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation("translation", { useSuspense: false })

  const [tournament, setTournament] = useState<Tournament | null>(null)
  const [participants, setParticipants] = useState<TournamentParticipant[]>([])
  const [analytics, setAnalytics] = useState<TournamentAnalytics | null>(null)
  const [referralData, setReferralData] = useState<TournamentReferralData | null>(null)
  const [loading, setLoading] = useState(true)

  const loadData = async () => {
    if (!id) return

    try {
      const [tournamentData, participantsData, analyticsData] = await Promise.all([
        TournamentService.getTournament(id),
        TournamentService.getTournamentParticipants(id, 1, 50, { sortRow: "current_rank", sortOrder: "asc" }),
        TournamentService.getTournamentAnalytics(id)
      ])

      setTournament(tournamentData || null)
      setParticipants(participantsData?.data || [])
      setAnalytics(analyticsData || null)

      // Load referral data if referrals are enabled
      if (tournamentData && (tournamentData as any).enable_referrals) {
        const refData = await TournamentService.getReferralRewards(id)
        setReferralData(refData || null)
      }
    } catch (error) {
      toast.error("Failed to load tournament data!")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()

    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      loadData()
    }, 10000)

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [id])

  const handleEndTournament = async () => {
    if (!id || !confirm("Are you sure you want to end this tournament early?")) return

    try {
      await TournamentService.endTournament(id)
      toast.success("Tournament ended successfully!")
      await loadData()
    } catch (error) {
      toast.error("Failed to end tournament!")
    }
  }

  if (loading) {
    return (
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("pages.tournamentMonitor.title")} />
        <div className="flex items-center justify-center py-16">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    )
  }

  if (!tournament) {
    return (
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("pages.tournamentMonitor.title")} />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">{t("tournaments.notFound")}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
      <TopBar title={t("tournaments.monitorTitle", { name: tournament.name })} />

      <div className="mt-4 space-y-6">
        <TournamentHeader tournament={tournament} onEndTournament={handleEndTournament} />

        {analytics && (
          <TournamentAnalyticsCards
            analytics={analytics}
            currency={tournament.currency}
            currencyRate={tournament.currency_rate}
          />
        )}

        {/* Chat Monitor */}
        {id && (
          <TournamentChatMonitor
            tournamentId={id}
            enableChat={tournament.enable_chat}
            chatSlowModeSeconds={(tournament as any).chat_slow_mode_seconds ?? 0}
            onToggleChat={async (enabled) => {
              try {
                await TournamentService.updateTournament(id, { enable_chat: enabled } as any)
                toast.success(`Chat ${enabled ? "enabled" : "disabled"}`)
                await loadData()
              } catch {
                toast.error("Failed to update chat setting")
              }
            }}
            onSlowModeChange={async (seconds) => {
              try {
                await TournamentService.updateTournament(id, { chat_slow_mode_seconds: seconds } as any)
                toast.success(`Slow mode ${seconds === 0 ? "disabled" : `set to ${seconds}s`}`)
                await loadData()
              } catch {
                toast.error("Failed to update slow mode")
              }
            }}
          />
        )}

        {referralData && <TournamentReferralActivity referralData={referralData} />}

        <TournamentLeaderboard
          participants={participants}
          currency={tournament.currency}
          currencyRate={tournament.currency_rate}
        />
      </div>
    </div>
  )
}
